import type { ApiError } from '~/types';

import { clearTokens, getRefreshToken, setTokensFromRefresh, getAccessToken } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

/**
 * Global error listener for API failures.
 * Used by analytics to track client-side errors without coupling
 * the API client to analytics directly.
 */
type ApiErrorListener = (endpoint: string, statusCode: number, message: string) => void;
const errorListeners: ApiErrorListener[] = [];

export function onApiError(listener: ApiErrorListener): () => void {
  errorListeners.push(listener);
  return () => {
    const index = errorListeners.indexOf(listener);
    if (index !== -1) errorListeners.splice(index, 1);
  };
}

function notifyErrorListeners(endpoint: string, statusCode: number, message: string): void {
  for (const listener of errorListeners) {
    try {
      listener(endpoint, statusCode, message);
    } catch {
      // Error listeners should never break API client
    }
  }
}

/**
 * Custom error class for API errors with status code and structured message.
 */
export class ApiRequestError extends Error {
  public statusCode: number;
  public errorBody: ApiError | null;

  constructor(statusCode: number, message: string, errorBody: ApiError | null = null) {
    super(message);
    this.name = 'ApiRequestError';
    this.statusCode = statusCode;
    this.errorBody = errorBody;
  }
}

// =============================================================================
// Token Refresh (direct fetch — NOT through apiClient to avoid recursion)
// =============================================================================

/** Prevents concurrent refresh attempts */
let refreshPromise: Promise<boolean> | null = null;

/**
 * Calls the public refresh endpoint directly via fetch.
 * Returns true if refresh succeeded, false otherwise.
 */
export async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  // Deduplicate concurrent refresh calls
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/public/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        // Refresh token is invalid/expired — clear everything
        clearTokens();
        return false;
      }

      const data = await response.json();
      setTokensFromRefresh(data);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// =============================================================================
// API Client
// =============================================================================

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  token?: string | null;
  /** Skip 401 retry (set internally to prevent infinite loops) */
  _skipRetry?: boolean;
}

/**
 * API client fetch wrapper.
 * - Configurable base URL via VITE_API_BASE_URL
 * - JSON request/response handling
 * - Optional auth header injection
 * - Structured error handling with ApiRequestError
 * - Automatic 401 retry: on 401, attempts token refresh then retries once
 */
export async function apiClient<T>(
  endpoint: string,
  options: FetchOptions = {},
): Promise<T> {
  const { body, token, _skipRetry, headers: customHeaders, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...((customHeaders as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // 401 Interceptor: try refresh then retry once
  if (response.status === 401 && !_skipRetry && token) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const newToken = getAccessToken();
      return apiClient<T>(endpoint, {
        ...options,
        token: newToken,
        _skipRetry: true,
      });
    }
    // Refresh failed — fall through to normal error handling
  }

  if (!response.ok) {
    let errorBody: ApiError | null = null;
    try {
      errorBody = await response.json() as ApiError;
    } catch {
      // Response body may not be JSON
    }

    const errorMessage = errorBody?.message || `API request failed with status ${response.status}`;
    notifyErrorListeners(endpoint, response.status, errorMessage);

    throw new ApiRequestError(
      response.status,
      errorMessage,
      errorBody,
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}
